import { ReactElement, useRef, useState } from "react";
import { Form, Tabs, Tab } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FieldErrors, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  AddOrEditItemProps,
  ProviderFormValidationCommon,
  mapToAddPaymentMethodConfiguration,
  mapToPaymentMethodConfiguration,
} from "../ProvidersCommon/Common";
import { PageTitle } from "../../../common/PageTitle";
import { StripePaymentProviderConfiguration } from "../../../../models/paymentProviders/stripe/stripeModels";

import { addConfiguration, updateConfiguration } from "../../../../services/paymentProviders/paymentProviders.service";

import { tPaymentProviders } from "../../../../i18n";
import { DefaultProvider } from "../DefaultProvider";
import PaymentProviderFiltersDialog from "../ProvidersCommon/PaymentProviderFiltersDialog";
import ItemFilterGroup from "../../../../models/itemFiltering/itemFilterGroup";
import { PaymentProvider } from "../../../../models/paymentProviders/paymentProvider";

import {
  CurrentFormModel,
  CurrentPaymentMethodType,
  CurrentPaymentProviderConfiguration,
  CurrentProviderDisplayName,
  mapToAddConfiguration,
  mapToUpdateConfiguration,
} from "./stripeConfiguration";
import StripeGeneralSettingsTab from "./StripeGeneralSettingsTab";
import StripeOptionsTabs from "./StripeOptionsTabs";
import { TabArray, selectTabFromValidationErrors } from "../../../../helpers/hookForms/hookFormHelpers";
import ProviderButtonToolbar from "../ProvidersCommon/ProviderButtonToolbar";
import TabRef from "../../../../components/Tabs/TabWithReference";

const EventKeyGeneralSettings = "general";
const EventKeyOptions = "options";

interface CurrentItemProps extends AddOrEditItemProps<CurrentPaymentProviderConfiguration, CurrentFormModel> {
  data?: StripePaymentProviderConfiguration;
  paymentProvider?: PaymentProvider;
  onSave: () => void;
}

/*
 * Form validation schema
 */
const validationSchema = Yup.object({
  ...ProviderFormValidationCommon,
  stripeAccountId: Yup.string().notRequired(),
  useStripeCustomer: Yup.boolean(),
  storePayerPaymentMethod: Yup.boolean(),
  saveCardOptionDefault: Yup.boolean(),
}).required();

/*
 * The Stripe add/edit component
 */
const StripeItem = ({ data, paymentProvider, onSave }: CurrentItemProps): ReactElement => {
  const { t } = useTranslation(tPaymentProviders.ns);
  const [selectedTab, setSelectedTab] = useState<string | null>(EventKeyGeneralSettings);

  // For now Stripe only supports one payment method type
  const paymentMethodConfiguration = data?.paymentMethodConfigurations.find(
    (x) => x.paymentMethodType === CurrentPaymentMethodType
  );

  const [filterGroup, setFilterGroup] = useState<ItemFilterGroup | undefined>(paymentMethodConfiguration?.filterGroup);
  const [showFilterDialog, setShowFilterDialog] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const tabRefs = useRef<TabArray>([]);

  const providerId = data?.id;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({ resolver: yupResolver(validationSchema) });

  // Called when the save button is pressed
  const handleSave = async (model: CurrentFormModel): Promise<void> => {
    setSaving(true);

    if (!data) {
      const addConfig = mapToAddConfiguration(model);
      const addPaymentMethod = mapToAddPaymentMethodConfiguration(CurrentPaymentMethodType, model.enabled, filterGroup);
      addConfig.paymentMethodConfigurations = [addPaymentMethod];
      if (await addConfiguration(addConfig).finally(() => setSaving(false))) {
        onSave();
      }
    } else {
      const configuration = mapToUpdateConfiguration(data, model);
      const paymentMethod = mapToPaymentMethodConfiguration(paymentMethodConfiguration!, model.enabled, filterGroup);
      configuration.paymentMethodConfigurations = [paymentMethod];
      await updateConfiguration(configuration).finally(() => setSaving(false));
      onSave();
    }
  };

  // Handles valuidate errors on the form, and selects the tab with the first error
  const handleValidationErrors = (errorData: FieldErrors<CurrentFormModel>): void => {
    selectTabFromValidationErrors(tabRefs, errorData, setSelectedTab);
  };

  return (
    <section className='custom-connect-page'>
      <DefaultProvider paymentProviderId={providerId} allowSetDefault>
        <PageTitle title={t("paymentProviders.titleEditConfiguration", { providerName: CurrentProviderDisplayName })} />
      </DefaultProvider>
      <Form
        id='stripe-form'
        className='mt-2'
        noValidate
        onSubmit={handleSubmit(
          (submitData) => handleSave(submitData as CurrentFormModel),
          (errorData) => handleValidationErrors(errorData)
        )}
      >
        <Tabs
          defaultActiveKey={EventKeyGeneralSettings}
          className='mb-2'
          activeKey={selectedTab ?? EventKeyGeneralSettings}
          onSelect={(tab) => setSelectedTab(tab)}
        >
          <Tab eventKey={EventKeyGeneralSettings} title={t("commonTableTitles.generalSettingsTabTitle")}>
            <TabRef eventKey={EventKeyGeneralSettings} index={0} tabRefs={tabRefs}>
              <StripeGeneralSettingsTab
                data={data}
                paymentProvider={paymentProvider}
                itemFilterGroup={filterGroup}
                onShowFilterDialog={() => setShowFilterDialog(true)}
                register={register}
                setValue={setValue}
                errors={errors}
              />
            </TabRef>
          </Tab>
          <Tab eventKey={EventKeyOptions} title={t("commonTableTitles.optionsTabTitle")}>
            <TabRef eventKey={EventKeyOptions} index={1} tabRefs={tabRefs}>
              <StripeOptionsTabs data={data} register={register} watch={watch} setValue={setValue} />
            </TabRef>
          </Tab>
        </Tabs>
        <ProviderButtonToolbar saving={saving} />
      </Form>
      <PaymentProviderFiltersDialog
        filterGroup={filterGroup}
        show={showFilterDialog}
        onUpdate={(group) => {
          setShowFilterDialog(false);
          setFilterGroup(group);
        }}
        onClose={() => setShowFilterDialog(false)}
      />
    </section>
  );
};

StripeItem.defaultProps = {
  data: undefined,
  paymentProvider: undefined,
};

export default StripeItem;
